import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';

const C = {
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

interface Session {
  name: string;
  email: string;
  device_id: string;
  app_version: string;
  platform: string;
  last_seen: string;
  minutes_ago: number;
  country: string;
  city: string;
}
interface Resp {
  live_5m: number;
  last_30m: number;
  today: number;
  last_24h: number;
  sessions: Session[];
  resolution_minutes: number;
}

function ago(mins: number): string {
  if (mins < 1) return 'just now';
  if (mins < 60) return `${Math.round(mins)} min ago`;
  const h = mins / 60;
  if (h < 24) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function ActiveSessions({ token, isMobile }: { token: string; isMobile: boolean }) {
  const [d, setD] = useState<Resp | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/active-sessions`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setD((j.data ?? j) as Resp);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
  }, [token]);

  // Poll while the dashboard is open so "live" actually means live.
  useEffect(() => {
    load();
    const t = setInterval(load, 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const card: React.CSSProperties = {
    background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px',
  };
  const td: React.CSSProperties = {
    textAlign: 'left', padding: '10px 12px', fontSize: 13.5, color: C.text,
    borderTop: `1px solid ${C.border}`, whiteSpace: 'nowrap',
  };
  const th: React.CSSProperties = {
    textAlign: 'left', padding: '10px 12px', fontSize: 11.5, color: C.sub,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
  };

  if (error) return <div style={{ color: '#F87171', fontSize: 13.5, marginTop: 20 }}>{error}</div>;
  if (!d) return null;

  const liveNow = d.live_5m > 0;

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{
          width: 9, height: 9, borderRadius: '50%',
          background: liveNow ? '#34D399' : '#3a3a3f',
          boxShadow: liveNow ? '0 0 0 4px rgba(52,211,153,0.15)' : 'none',
        }} />
        <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: C.text }}>
          {liveNow ? `${d.live_5m} using the app now` : 'Nobody using the app right now'}
        </h2>
        <span style={{ marginLeft: 'auto', color: C.sub, fontSize: 12 }}>refreshes every minute</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
        {[
          { label: 'Live now', value: d.live_5m, color: liveNow ? '#34D399' : undefined },
          { label: 'Last 30 min', value: d.last_30m },
          { label: 'Today', value: d.today },
          { label: 'Last 24 hours', value: d.last_24h },
        ].map((k) => (
          <div key={k.label} style={card}>
            <div style={{ color: C.sub, fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: k.color || C.text }}>{k.value}</div>
          </div>
        ))}
      </div>

      <p style={{ color: C.sub, fontSize: 12.5, margin: '0 0 10px', lineHeight: 1.6 }}>
        Counts devices that made an API request, so it includes people browsing their garden, not just
        those who scanned. Activity is recorded at most once every {d.resolution_minutes} minutes per
        device, so "live now" means within the last few minutes rather than to the second.
      </p>

      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 560 }}>
          <thead>
            <tr>
              <th style={th}>User</th>
              <th style={th}>Where</th>
              <th style={th}>Build</th>
              <th style={{ ...th, textAlign: 'right' }}>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {d.sessions.map((s) => {
              const isLive = s.minutes_ago < 5;
              return (
                <tr key={s.device_id} style={{ background: isLive ? C.panelAlt : 'transparent' }}>
                  <td style={td}>
                    <span style={{ fontWeight: isLive ? 700 : 500 }}>{s.name}</span>
                    {isLive && <span style={{ marginLeft: 8, fontSize: 10.5, color: '#34D399', fontWeight: 800 }}>LIVE</span>}
                  </td>
                  <td style={{ ...td, color: C.sub, fontSize: 12.5 }}>
                    {[s.city, s.country].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td style={{ ...td, color: C.sub, fontSize: 12.5 }}>{s.app_version || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', color: isLive ? '#34D399' : C.sub, fontSize: 12.5 }}>
                    {ago(s.minutes_ago)}
                  </td>
                </tr>
              );
            })}
            {d.sessions.length === 0 && (
              <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: C.sub }}>No activity in the last 24 hours</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ActiveSessions;
